import type { Account } from '@/common/types'

/**
 * Account Switching State Machine
 *
 * Manages the lifecycle of an account switch operation with race condition safeguards.
 * Uses session tokens to invalidate stale async settlements.
 */

export enum AccountSwitchState {
    IDLE = 'IDLE',
    SWITCHING = 'SWITCHING',
}

export interface AccountSwitchStateMachineState {
    state: AccountSwitchState
    targetAccountId: string | null
    sessionToken: number
}

export interface AccountSwitchContext {
    currentAccountId: string | null
    targetAccountId: string | null
    availableAccounts: Account[]
    primaryAccount: Account | null
    sessionToken: number
    isUserPending: boolean
    userError: unknown | null
}

/**
 * Validates whether a session token is still active.
 * Used to ignore stale async settlements from previous switch attempts.
 */
export const isSessionTokenActive = (capturedToken: number, currentToken: number): boolean => {
    return capturedToken === currentToken
}

/**
 * Determines if a target account is known on the server.
 * Checks both the main account list and primary account.
 */
export const isTargetAccountKnown = (
    targetId: string | null,
    availableAccounts: Account[],
    primaryAccount: Account | null,
): boolean => {
    if (!targetId) return false
    if (primaryAccount?.accountId === targetId) return true
    return availableAccounts.some((a) => a.accountId === targetId)
}

/**
 * Account switch state transitions
 */

/**
 * IDLE → SWITCHING: User initiates account switch
 */
export const startAccountSwitch = (
    currentState: AccountSwitchStateMachineState,
    targetAccountId: string | null,
): AccountSwitchStateMachineState => {
    if (!targetAccountId) {
        return currentState
    }

    return {
        state: AccountSwitchState.SWITCHING,
        targetAccountId,
        sessionToken: currentState.sessionToken + 1,
    }
}

/**
 * SWITCHING → IDLE: Switch operation completes
 *
 * Completion conditions:
 * 1. Target account is confirmed on server
 * 2. User data fetch fails (give up)
 */
export const finishAccountSwitch = (currentState: AccountSwitchStateMachineState): AccountSwitchStateMachineState => {
    return {
        state: AccountSwitchState.IDLE,
        targetAccountId: null,
        sessionToken: currentState.sessionToken,
    }
}

/**
 * Determines if we should finish the current switch
 * based on settlement conditions.
 */
export const shouldFinishSwitch = (
    state: AccountSwitchStateMachineState,
    context: AccountSwitchContext,
    capturedSessionToken: number,
): boolean => {
    if (state.state !== AccountSwitchState.SWITCHING) {
        return false
    }

    // Check if token is still active
    if (!isSessionTokenActive(capturedSessionToken, context.sessionToken)) {
        return false
    }

    // Check if we're at the target account
    if (context.currentAccountId !== state.targetAccountId) {
        return false
    }

    // Condition 1: Target is known on server
    if (isTargetAccountKnown(state.targetAccountId, context.availableAccounts, context.primaryAccount)) {
        return true
    }

    // Condition 2: User data fetch failed (give up)
    if (!context.isUserPending && context.userError) {
        return true
    }

    return false
}

/**
 * Diagnostic utilities for debugging state machine
 */
export const debugStateMachine = (state: AccountSwitchStateMachineState, context: AccountSwitchContext): string => {
    return `[AccountSwitch] state=${state.state} target=${state.targetAccountId} token=${state.sessionToken} current=${context.currentAccountId}`
}
